import { ApiError, type InviteDetails } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';

/**
 * Where an invitation link lands: `/invite#<token>`.
 *
 * The token is in the fragment so it never reaches a server log; it is read
 * here and sent to the API in request bodies. The page is public because most
 * invitees have no account yet.
 */
export function AcceptInvitePage() {
  const { user, client, loading } = useAuth();
  const location = useLocation();
  const token = location.hash.replace(/^#/, '');

  const details = useQuery({
    queryKey: ['invite', token],
    queryFn: () => client.lookupInvite(token),
    enabled: token !== '',
    retry: false,
    // Accepting spends the link, so a refetch afterwards would call it invalid.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  if (loading || details.isLoading) return <Frame title="Invitation" description="Loading…" />;

  // Only a 404 says the link itself is no good. Anything else — the API out of
  // reach, a server error — says nothing about the link, so offer a retry
  // rather than telling someone their invitation is dead.
  const linkIsDead = details.error instanceof ApiError && details.error.status === 404;
  if (details.isError && !linkIsDead) {
    return (
      <Frame title="Couldn't load this invitation" description={details.error.message}>
        <Button variant="outline" className="w-full" onClick={() => void details.refetch()}>
          Try again
        </Button>
      </Frame>
    );
  }

  if (!details.data) {
    return (
      <Frame
        title="This invitation can't be used"
        description="The link is invalid, has expired, or has already been used. Ask whoever invited you for a new one."
      />
    );
  }

  const invite = details.data;
  const article = invite.role === 'member' ? 'a' : 'an';

  return (
    <Frame
      title={`Join ${invite.organization_name}`}
      description={`${invite.inviter_name ?? 'Someone'} invited ${invite.email} to join as ${article} ${invite.role}.`}
    >
      {user ? (
        <SignedIn invite={invite} token={token} />
      ) : invite.account_exists ? (
        <SignInFirst invite={invite} />
      ) : (
        <CreateAccount invite={invite} token={token} />
      )}
    </Frame>
  );
}

function SignedIn({ invite, token }: { invite: InviteDetails; token: string }) {
  const { user, client, logout } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const accept = useMutation({
    mutationFn: () => client.acceptInvite(token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate('/', { replace: true });
    },
  });

  if (user && user.email.toLowerCase() !== invite.email.toLowerCase()) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          You&apos;re signed in as {user.email}. Sign out to accept this invitation as{' '}
          {invite.email}.
        </p>
        <Button variant="outline" className="w-full" onClick={() => void logout()}>
          Sign out
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Button className="w-full" onClick={() => accept.mutate()} disabled={accept.isPending}>
        Accept invitation
      </Button>
      {accept.error && (
        <p role="alert" className="text-sm text-destructive">
          {(accept.error as Error).message}
        </p>
      )}
    </div>
  );
}

/** The address has an account: sign in, then come back here to accept. */
function SignInFirst({ invite }: { invite: InviteDetails }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {invite.email} already has an account. Sign in to accept.
      </p>
      <Button className="w-full" onClick={() => navigate('/login', { state: { from: location } })}>
        Sign in
      </Button>
    </div>
  );
}

function CreateAccount({ invite, token }: { invite: InviteDetails; token: string }) {
  const { registerWithInvite } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');

  const register = useMutation({
    mutationFn: () => registerWithInvite({ token, name, password }),
    onSuccess: () => navigate('/', { replace: true }),
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    register.mutate();
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="invite-email">Email</Label>
        <Input id="invite-email" value={invite.email} readOnly disabled />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          autoComplete="name"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
          minLength={12}
          autoComplete="new-password"
        />
        <p className="text-xs text-muted-foreground">At least 12 characters.</p>
      </div>
      {register.error && (
        <p role="alert" className="text-sm text-destructive">
          {(register.error as Error).message}
        </p>
      )}
      <Button type="submit" className="w-full" disabled={register.isPending}>
        {register.isPending ? 'Please wait…' : 'Create account and join'}
      </Button>
    </form>
  );
}

function Frame({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col px-4">
      <main className="grid flex-1 place-items-center py-8">
        <Card className="w-full max-w-sm">
          <CardHeader className="items-center text-center">
            <span className="grid size-10 place-items-center rounded-lg bg-primary text-primary-foreground">
              <AlertTriangle className="size-5" />
            </span>
            <CardTitle className="text-xl">{title}</CardTitle>
            {description && <CardDescription>{description}</CardDescription>}
          </CardHeader>
          {children && <CardContent>{children}</CardContent>}
        </Card>
      </main>
      <Footer />
    </div>
  );
}
